/**
 * 점주 콘솔 v2 — 메뉴 정의 + capability 기반 navigation builder
 *
 * 이 파일이 메뉴/라우트 접근 조건의 단일 소스다.
 * - navigation.js: 메뉴 데이터(순수 JS, React 의존 없음 → unit test 대상)
 * - routes.js:     key → 페이지 컴포넌트 바인딩
 *
 * 노출 조건 (canAccessItem): category 일치 + requiredCapabilities 충족 + requiredPermissions 충족
 *
 * ⚠️ 사이드바에서 메뉴를 숨기는 것은 UX용이다. 실제 보안은 백엔드 permission 검증이 필요하다.
 *    (TODO: backend permission 연동 PR에서 API 레벨 검증 추가 — permissions/check.js 주석 참조)
 */
import { canAccessItem } from './permissions/check';

export const CATEGORY_LABELS = {
  carwash: '세차장',
  academy: '학원',
  gym: '헬스장',
  laundry: '세탁소',
};

/** admin role → 콘솔 표기 라벨 (backend admin.schema enum 기준) */
export const ROLE_LABELS = {
  master: '마스터',
  manager: '점주',
  staff: '직원',
  readonly: '조회 전용',
};

/**
 * item shape:
 *   key                  고유 키 (routes.js 컴포넌트 바인딩 키)
 *   label                사이드바 표기명
 *   path                 /owner-v2 하위 상대 경로 ('' = index → 대시보드)
 *   section              'core'(공통) | 'module'(업종별)
 *   categories           노출 업종 목록 (생략 = 모든 업종 공통)
 *   requiredCapabilities store.capabilities 요구값
 *   requiredPermissions  session.permissions 요구값
 */
export const NAVIGATION_ITEMS = [
  // ── core 공통 ─────────────────────────────────────────
  {
    key: 'dashboard',
    label: '대시보드',
    path: '',
    section: 'core',
    requiredPermissions: ['dashboard.read'],
  },
  {
    key: 'members',
    label: '회원 관리',
    path: 'members',
    section: 'core',
    requiredPermissions: ['members.read'],
  },
  {
    key: 'membershipPlans',
    label: '멤버십 상품',
    path: 'membership-plans',
    section: 'core',
    requiredPermissions: ['membership.read'],
  },
  {
    key: 'usageHistory',
    label: '이용 내역',
    path: 'usage-history',
    section: 'core',
    requiredPermissions: ['usage.read'],
  },
  {
    key: 'payments',
    label: '결제 관리',
    path: 'payments',
    section: 'core',
    requiredPermissions: ['payment.read'],
  },
  {
    key: 'notices',
    label: '공지사항',
    path: 'notices',
    section: 'core',
    requiredPermissions: ['notice.read'],
  },
  {
    key: 'settings',
    label: '설정',
    path: 'settings',
    section: 'core',
    requiredPermissions: ['settings.manage'],
  },
  {
    // audit.read 는 master 에게만 부여되므로 자연히 master 전용 메뉴가 된다
    key: 'auditLogs',
    label: '개인정보 열람 기록',
    path: 'audit-logs',
    section: 'core',
    requiredPermissions: ['audit.read'],
  },
  {
    // launchSignups.read 도 master 전용 — 출시 알림 신청자 명단 (플랫폼 스코프 PII)
    key: 'launchSignups',
    label: '출시 알림 신청',
    path: 'launch-signups',
    section: 'core',
    requiredPermissions: ['launchSignups.read'],
  },
  {
    // websiteStores.manage 도 master 전용 — 고객 앱/웹 공개 노출 매장 통제
    key: 'websiteStores',
    label: '매장 노출 관리',
    path: 'website-stores',
    section: 'core',
    requiredPermissions: ['websiteStores.manage'],
  },

  // ── carwash 모듈 ──────────────────────────────────────
  {
    key: 'vehicles',
    label: '차량 관리',
    path: 'vehicles',
    section: 'module',
    categories: ['carwash'],
    requiredPermissions: ['vehicle.read'],
  },
  {
    key: 'lprLogs',
    label: 'LPR 인식 로그',
    path: 'lpr-logs',
    section: 'module',
    categories: ['carwash'],
    requiredCapabilities: ['lpr'],
    requiredPermissions: ['lpr.read'],
  },
  {
    key: 'kioskStatus',
    label: '키오스크 상태',
    path: 'kiosk-status',
    section: 'module',
    categories: ['carwash'],
    requiredCapabilities: ['kiosk'],
    requiredPermissions: ['kiosk.read'],
  },
  {
    key: 'washPrograms',
    label: '세차 프로그램',
    path: 'wash-programs',
    section: 'module',
    categories: ['carwash'],
    requiredCapabilities: ['washProgram'],
    requiredPermissions: ['washProgram.read'],
  },
  {
    key: 'rfCards',
    label: 'RF 카드',
    path: 'rf-cards',
    section: 'module',
    categories: ['carwash'],
    requiredCapabilities: ['rfCard'],
    requiredPermissions: ['rfCard.read'],
  },
  {
    key: 'dataQuality',
    label: '원천 · 귀속 점검',
    path: 'data-quality',
    section: 'module',
    categories: ['carwash'],
    // 읽기 전용 운영 점검은 대시보드 조회 권한을 사용한다. 실제 API 권한은 backend가 강제한다.
    requiredPermissions: ['dashboard.read'],
  },
  {
    key: 'jj001WashReports',
    label: 'jj001 세차 현황',
    path: 'jj001-wash-reports',
    section: 'module',
    categories: ['carwash'],
    // JJ001은 ModernWash 전주점의 현장 관측 보관본이다. 일반 세차장 메뉴에
    // 노출하지 않으며, 서버도 같은 지점 스코프를 다시 강제한다.
    storeCodes: ['jeonju'],
    // 원천 집계 조회만 제공하며, 별도 변경 권한을 요구하지 않는다.
    requiredPermissions: ['dashboard.read'],
  },

  // ── academy 모듈 ──────────────────────────────────────
  {
    key: 'students',
    label: '수강생 관리',
    path: 'students',
    section: 'module',
    categories: ['academy'],
    requiredPermissions: ['students.read'],
  },
  {
    key: 'parents',
    label: '학부모 관리',
    path: 'parents',
    section: 'module',
    categories: ['academy'],
    requiredPermissions: ['parents.read'],
  },
  {
    key: 'classes',
    label: '수업 관리',
    path: 'classes',
    section: 'module',
    categories: ['academy'],
    requiredPermissions: ['classes.read'],
  },
  {
    key: 'attendance',
    label: '출결 관리',
    path: 'attendance',
    section: 'module',
    categories: ['academy'],
    requiredCapabilities: ['attendance'],
    requiredPermissions: ['attendance.read'],
  },

  // ── gym 모듈 (Coming Soon — 라우트/placeholder만 존재) ──
  {
    key: 'gymAttendance',
    label: '출석 관리',
    path: 'gym-attendance',
    section: 'module',
    categories: ['gym'],
    requiredCapabilities: ['attendance'],
    requiredPermissions: ['attendance.read'],
  },
  {
    key: 'lockers',
    label: '락커 관리',
    path: 'lockers',
    section: 'module',
    categories: ['gym'],
    requiredPermissions: ['lockers.read'],
  },
  {
    key: 'lessons',
    label: '레슨 관리',
    path: 'lessons',
    section: 'module',
    categories: ['gym'],
    requiredPermissions: ['lessons.read'],
  },

  // ── laundry 모듈 (Coming Soon — 라우트/placeholder만 존재) ──
  {
    key: 'machines',
    label: '장비 관리',
    path: 'machines',
    section: 'module',
    categories: ['laundry'],
    requiredPermissions: ['machines.read'],
  },
  {
    key: 'orders',
    label: '주문 관리',
    path: 'orders',
    section: 'module',
    categories: ['laundry'],
    requiredPermissions: ['orders.read'],
  },
  {
    key: 'pickup',
    label: '수거/배송',
    path: 'pickup',
    section: 'module',
    categories: ['laundry'],
    requiredPermissions: ['pickup.read'],
  },
];

/**
 * capability 기반 navigation builder.
 * session이 볼 수 있는 메뉴만 필터링해 반환한다 (사이드바 노출용).
 */
export function buildNavigation(session, items = NAVIGATION_ITEMS) {
  return items.filter((item) => canAccessItem(session, item));
}

/**
 * 사이드바 렌더링용 — 노출 메뉴를 섹션별로 그룹핑.
 * 반환: [{ section: 'core'|'module', title, items: [...] }]  (빈 섹션은 제외)
 */
export function buildNavigationSections(session, items = NAVIGATION_ITEMS) {
  const visible = buildNavigation(session, items);
  const core = visible.filter((i) => i.section === 'core');
  const module = visible.filter((i) => i.section === 'module');
  const categoryLabel = CATEGORY_LABELS[session?.store?.category] || '업종';

  const sections = [];
  if (core.length > 0) sections.push({ section: 'core', title: '공통', items: core });
  if (module.length > 0)
    sections.push({
      section: 'module',
      title: `${categoryLabel} 메뉴`,
      items: module,
    });
  return sections;
}

/** path(상대 경로)로 메뉴 item 찾기 — 라우트 가드에서 사용 */
export function findItemByPath(path, items = NAVIGATION_ITEMS) {
  return items.find((item) => item.path === path) || null;
}
